/** Selection math for the List view's multi-select. Pure + exported so the
 *  select-all / prune-on-filter-change / header-state logic is unit-tested
 *  without mounting the table. */

/** Toggle one id in/out of the selection, returning a new Set. */
export function toggleSelection(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Select exactly the given ids (used by the header "select all visible"). */
export function selectAll(ids: readonly string[]): Set<string> {
  return new Set(ids);
}

/** Drop any selected id that is no longer visible (e.g. after a filter
 *  narrows the table). Returns the SAME reference when nothing changed so
 *  a `useState` setter can skip a needless re-render. */
export function pruneToVisible(
  selected: ReadonlySet<string>,
  visibleIds: Iterable<string>,
): Set<string> {
  const visible = visibleIds instanceof Set ? visibleIds : new Set(visibleIds);
  let changed = false;
  const pruned = new Set<string>();
  for (const id of selected) {
    if (visible.has(id)) pruned.add(id);
    else changed = true;
  }
  return changed ? pruned : (selected as Set<string>);
}

export interface HeaderSelectionState {
  /** Every visible row is selected (drives the header checkbox `checked`). */
  allSelected: boolean;
  /** Some but not all visible rows are selected (drives `indeterminate`). */
  someSelected: boolean;
}

export function headerSelectionState(
  visibleIds: readonly string[],
  selected: ReadonlySet<string>,
): HeaderSelectionState {
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selected.has(id));
  return { allSelected, someSelected };
}
